import { useQueryClient } from "@tanstack/react-query"

import { PublicUser, UserRelationV1 } from "@bullseye/types"

import { useUserContext } from "../providers/UserAuthProvider/UserProvider"
import { useGetRequest } from "./useRequest"
import { getBlockedUserIds } from "./useUserBlock"

type UserWithRelation = PublicUser & {
  relation: UserRelationV1
}

type UserData = {
  data: UserWithRelation[]
}

type ChatSearchOptions = {
  excludeBlocked?: boolean
}

export const useChatUserSearch = (
  name: string | undefined | null,
  options?: ChatSearchOptions,
) => {
  const { accessToken } = useUserContext()
  const client = useQueryClient()
  const excludeBlocked = options?.excludeBlocked !== false
  const result = useGetRequest<UserData>(
    {
      method: "GET",
      path: "v1/chat/users",
      params: {
        name,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    {
      enabled: !!accessToken && !!name,
    },
  )

  const dataResponse: UserData | undefined = result.data
  const { isLoading, error, refetch, key } = result

  // Client-side filter to exclude blocked users if API doesn't support it
  const blocked: ReadonlySet<string> = getBlockedUserIds()
  const list: UserWithRelation[] = Array.isArray(dataResponse?.data)
    ? dataResponse.data
    : []
  const filteredUsers = excludeBlocked
    ? list.filter((u) => !blocked.has(u.id))
    : list

  return {
    users: filteredUsers,
    isLoading,
    error,
    refetch,
    cancel: () => client.cancelQueries({ queryKey: key }),
  }
}
